/**
 * Products Domain Utilities
 */

import type {
  SKU,
  Variant,
  Category,
  ProductsFilter,
  ProductsStats,
  LocalizedString,
} from '../types'

// ============================================================================
// Localized String Helpers
// ============================================================================

export function getLocalizedName(
  name: LocalizedString | undefined,
  locale: 'en' | 'ar' = 'en'
): string {
  if (!name) return ''
  return locale === 'ar' && name.ar ? name.ar : name.en
}

export function formatLocalizedName(name: LocalizedString | undefined): string {
  if (!name) return ''
  if (name.ar) {
    return `${name.en} / ${name.ar}`
  }
  return name.en
}

// ============================================================================
// SKU Filters
// ============================================================================

export function filterSkusByStatus(skus: SKU[], status: 'all' | 'active' | 'inactive'): SKU[] {
  if (status === 'all') return skus
  return skus.filter((sku) => (status === 'active' ? sku.isActive : !sku.isActive))
}

export function filterSkusBySearch(skus: SKU[], search: string): SKU[] {
  if (!search.trim()) return skus
  const term = search.toLowerCase()
  return skus.filter((sku) => {
    const nameEn = sku.name.en.toLowerCase()
    const nameAr = sku.name.ar?.toLowerCase() || ''
    return nameEn.includes(term) || nameAr.includes(term)
  })
}

export function filterSkusByCategory(skus: SKU[], categoryId: string | undefined): SKU[] {
  if (!categoryId) return skus
  return skus.filter((sku) =>
    sku.categories?.some((cat) => cat.documentId === categoryId)
  )
}

export function applySkuFilters(skus: SKU[], filter: ProductsFilter): SKU[] {
  let result = filterSkusByStatus(skus, filter.status)
  result = filterSkusBySearch(result, filter.search)
  result = filterSkusByCategory(result, filter.categoryId)
  return result
}

// ============================================================================
// Variant Filters
// ============================================================================

export function filterVariantsByStatus(
  variants: Variant[],
  status: 'all' | 'active' | 'inactive'
): Variant[] {
  if (status === 'all') return variants
  return variants.filter((v) => (status === 'active' ? v.isActive : !v.isActive))
}

export function filterVariantsBySearch(variants: Variant[], search: string): Variant[] {
  if (!search.trim()) return variants
  const term = search.toLowerCase()
  return variants.filter((v) => {
    const nameEn = v.name.en.toLowerCase()
    const nameAr = v.name.ar?.toLowerCase() || ''
    const code = v.variantCode.toLowerCase()
    const barcode = v.barcode.toLowerCase()
    return (
      nameEn.includes(term) ||
      nameAr.includes(term) ||
      code.includes(term) ||
      barcode.includes(term)
    )
  })
}

export function filterVariantsByPackaging(
  variants: Variant[],
  packagingType: 'all' | 'bags' | 'bulk' | undefined
): Variant[] {
  if (!packagingType || packagingType === 'all') return variants
  return variants.filter((v) => v.packaging === packagingType)
}

export function filterVariantsBySkuId(
  variants: Variant[],
  skuDocumentId: string | undefined
): Variant[] {
  if (!skuDocumentId) return variants
  return variants.filter((v) => v.skuDocumentId === skuDocumentId)
}

export function applyVariantFilters(
  variants: Variant[],
  filter: ProductsFilter,
  skuDocumentId?: string
): Variant[] {
  let result = filterVariantsByStatus(variants, filter.status)
  result = filterVariantsBySearch(result, filter.search)
  result = filterVariantsByPackaging(result, filter.packagingType)
  if (skuDocumentId) {
    result = filterVariantsBySkuId(result, skuDocumentId)
  }
  return result
}

// ============================================================================
// Category Filters
// ============================================================================

export function filterCategoriesByStatus(
  categories: Category[],
  status: 'all' | 'active' | 'inactive'
): Category[] {
  if (status === 'all') return categories
  return categories.filter((c) => (status === 'active' ? c.isActive : !c.isActive))
}

export function filterCategoriesBySearch(categories: Category[], search: string): Category[] {
  if (!search.trim()) return categories
  const term = search.toLowerCase()
  return categories.filter((c) => {
    const nameEn = c.name.en.toLowerCase()
    const nameAr = c.name.ar?.toLowerCase() || ''
    const code = c.code?.toLowerCase() || ''
    return nameEn.includes(term) || nameAr.includes(term) || code.includes(term)
  })
}

// ============================================================================
// Stats Calculation
// ============================================================================

export function calculateProductsStats(
  skus: SKU[],
  variants: Variant[],
  categories: Category[]
): ProductsStats {
  return {
    totalSkus: skus.length,
    activeSkus: skus.filter((s) => s.isActive).length,
    inactiveSkus: skus.filter((s) => !s.isActive).length,
    totalVariants: variants.length,
    activeVariants: variants.filter((v) => v.isActive).length,
    totalCategories: categories.length,
  }
}

// ============================================================================
// Sorting
// ============================================================================

export function sortSkusByName(skus: SKU[], locale: 'en' | 'ar' = 'en'): SKU[] {
  return [...skus].sort((a, b) => {
    const nameA = getLocalizedName(a.name, locale)
    const nameB = getLocalizedName(b.name, locale)
    return nameA.localeCompare(nameB)
  })
}

export function sortVariantsByCode(variants: Variant[]): Variant[] {
  return [...variants].sort((a, b) => a.variantCode.localeCompare(b.variantCode))
}

export function sortCategoriesByName(categories: Category[], locale: 'en' | 'ar' = 'en'): Category[] {
  return [...categories].sort((a, b) => {
    const nameA = getLocalizedName(a.name, locale)
    const nameB = getLocalizedName(b.name, locale)
    return nameA.localeCompare(nameB)
  })
}

// ============================================================================
// Formatting
// ============================================================================

export function formatWeight(weight: number): string {
  return `${weight} kg`
}

export function formatVariantDisplay(variant: Variant, locale: 'en' | 'ar' = 'en'): string {
  const name = getLocalizedName(variant.name, locale)
  return `${variant.variantCode} - ${name} (${formatWeight(variant.weight)})`
}
